import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { IconField, EyeIcon, EyeOffIcon } from '../../components/ui/IconField';

// Landing page for the password-reset email link. Supabase establishes a
// recovery session from the URL, so we can set a new password here.
export default function ResetPassword() {
  const { updatePassword, logout } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await updatePassword(password);
      setDone(true);
      await logout();
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#8b9bf0] via-[#677BE6] to-[#7450A9] px-4 py-8">
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-white/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -right-16 h-[30rem] w-[30rem] rounded-full bg-[#5d3f8a]/40 blur-3xl" />

      <div className="relative z-10 w-full max-w-sm" style={{ animation: 'fadeUp 0.5s ease-out' }}>
        <div className="rounded-2xl border border-white/15 bg-slate-900/30 backdrop-blur-2xl p-6 sm:p-8 shadow-2xl ring-1 ring-white/10">
          <div className="text-center">
            <div className="mx-auto inline-flex rounded-xl bg-white px-4 py-2 shadow-lg">
              <img src="/cysops-logo.png" alt="CysOps" className="h-9 sm:h-10 w-auto" />
            </div>
            <p className="mt-3 text-sm text-white/70">Set a new password</p>
          </div>

          {error && <div className="mt-5 rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-200 ring-1 ring-red-400/30">{error}</div>}

          {done ? (
            <div className="mt-5 rounded-lg bg-green-500/15 px-3 py-2 text-sm text-green-100 ring-1 ring-green-400/30">
              Password updated. Redirecting you to sign in…
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-white/90">New password</label>
                <IconField
                  type={showPw ? 'text' : 'password'} placeholder="••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password"
                  right={(
                    <button type="button" onClick={() => setShowPw((v) => !v)} className="p-1 text-white/50 hover:text-white" aria-label={showPw ? 'Hide password' : 'Show password'}>
                      {showPw ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  )}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-white/90">Confirm password</label>
                <IconField
                  type={showPw ? 'text' : 'password'} placeholder="••••••••"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password"
                />
              </div>
              <Button type="submit" disabled={busy} className="w-full">
                {busy ? 'Updating…' : 'Update password'}
              </Button>
              <button type="button" onClick={() => navigate('/login')} className="block w-full text-center text-sm font-medium text-white/60 hover:text-white">
                ← Back to sign in
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
